<template>
  <div class="w-100 h-100 bg-gray100 d-flex align-items-center justify-content-center">
    <div class="mt-n5 w-25 bg-white rounded-lg p-5 shadow-custom">
      <h5 class="alert-heading">
        {{ $t('register.title') }}
      </h5>
      <div class="w-100 text-left">
        <b-form @submit="submit">
          <b-form-group>
            <h6 class="font-size-md">
              <span class="ml-n1 text-danger">*</span>
              {{ $t('register.account.label') }}
            </h6>
            <b-form-input
              v-model="form.account.value"
              :placeholder="$t('register.account.placeholder')"
              size="sm"
              type="text"
              class="rounded-pill"
              :class="!form.account.dirty || form.account.valid ? '': 'is-invalid'"
              autocomplete="off"
              max-length="50"
              required
              @focus="onFocus('account')"
              @input="onAccountInput"
              @blur="onAccountBlur"
            />
            <div v-if="form.account.focus && (!form.account.dirty || form.account.valid)" class="mt-1 font-size-sm text-gray600">
              {{ $t('register.account.hint') }}
            </div>
            <div class="invalid-feedback">
              {{ form.account.feedback }}
            </div>
          </b-form-group>
          <b-form-group>
            <h6 class="font-size-md">
              <span class="ml-n1 text-danger">*</span>
              {{ $t('register.password.label') }}
            </h6>
            <div class="input-icon input-icon-sm">
              <b-form-input
                v-model="form.password.value"
                :type="form.password.showPassword ? 'text' : 'password'"
                :placeholder="$t('register.password.placeholder')"
                size="sm"
                class="rounded-pill pr-5"
                :class="!form.password.dirty || form.password.valid ? '': 'is-invalid'"
                @focus="onFocus('password')"
                @input="onPwdInput"
                @blur="onBlur('password')"
              />
              <div v-if="form.password.focus" class="input-icon-group">
                <a href="#" tabindex="-1" @mousedown="toggleVisibility('password', $event)">
                  <span class="material-icons font-size-base text-secondary" :class="!form.password.dirty || form.password.valid ? '' : 'mr-3'">
                    {{ form.password.showPassword ? 'visibility_off' : 'visibility' }}
                  </span>
                </a>
              </div>
              <div v-if="form.password.focus && (!form.password.dirty || form.password.valid)" class="mt-1 font-size-sm text-gray600">
                {{ $t('register.password.hint') }}
              </div>
              <div class="invalid-feedback">
                {{ form.password.feedback }}
              </div>
            </div>
          </b-form-group>
          <b-form-group>
            <h6 class="font-size-md">
              <span class="ml-n1 text-danger">*</span>
              {{ $t('register.confirmPassword.label') }}
            </h6>
            <div class="input-icon input-icon-sm">
              <b-form-input
                v-model="form.confirmPassword.value"
                size="sm"
                class="rounded-pill pr-5"
                :class="!form.confirmPassword.dirty || form.confirmPassword.valid ? '': 'is-invalid'"
                :type="form.confirmPassword.showPassword ? 'text' : 'password'"
                :placeholder="$t('register.confirmPassword.placeholder')"
                @focus="onFocus('confirmPassword')"
                @input="onConfirmPwdInput"
                @blur="onBlur('confirmPassword')"
              />
              <div v-if="form.confirmPassword.focus" class="input-icon-group">
                <a href="#" tabindex="-1" @mousedown="toggleVisibility('confirmPassword', $event)">
                  <span class="material-icons font-size-base text-secondary" :class="!form.confirmPassword.dirty || form.confirmPassword.valid ? '' : 'mr-3'">
                    {{ form.confirmPassword.showPassword ? 'visibility_off' : 'visibility' }}
                  </span>
                </a>
              </div>
              <div class="invalid-feedback">
                {{ form.confirmPassword.feedback }}
              </div>
            </div>
          </b-form-group>
          <vue-captcha ref="captcha" :sitekey="captchaSiteKey" type="recaptcha" :options="{ size: 'invisible' }" />
          <div class="mt-3 text-center">
            <button type="submit" :disabled="!form.valid" class="mx-1 btn btn-sm btn-primary rounded-pill" @click="submit">
              <span class="px-3">{{ $t('register.submit') }}</span>
            </button>
          </div>
        </b-form>
      </div>
    </div>
  </div>
</template>

<script>
import LoginStatusType from '@/objects/LoginStatusType';
import { VerifyAsync, LoginAsync, RefreshAsync } from '@/services/AuthenticationService';
import PermissionService from '@/services/PermissionService';
import { PasswordFormatValidate } from '@/services/AccountService';
import store from '../store';

export default {
  props: {
    redirect: {
      type: String
    },
    code: {
      type: String
    }
  },
  async beforeRouteEnter(to, from, next) {
    store.dispatch('loadLoginStatus');
    const status = await VerifyAsync();

    if (status === LoginStatusType.Success) {
      next({ path: to.query.redirect || '/' });
    } else {
      next();
    }
  },
  data() {
    const vm = this;
    return {
      captchaSiteKey: vm.$env.VUE_APP_Captcha_SiteKey,
      form: {
        account: {
          validate() {
            return this.validation.min.validation(this.value);
          },
          get feedback() {
            return `${this.validation.min.feedback || this.validation.duplicate.feedback}`;
          },
          validation: {
            min: {
              get feedback() {
                return this.valid ? '' : vm.$t('register.account.feedback.min');
              },
              validation(val) {
                if (val.length >= 6 && !/\s/g.test(val)) {
                  this.valid = true;
                } else {
                  this.valid = false;
                }
                return this.valid;
              }
            },
            duplicate: {
              get feedback() {
                return this.valid ? '' : vm.$t('register.account.feedback.duplicate');
              },
              async validation(val) {
                if (val === '' || val.length < 6) {
                  this.valid = true;
                } else {
                  this.valid = !await PermissionService.IsAccountExistsAsync(val);
                }

                return this.valid;
              },
              valid: true
            }
          },
          dirty: false,
          valid: false,
          value: '',
          focus: false
        },
        password: {
          validate(account) {
            return this.validation.regex.validation(this.value) && this.validation.accountDuplicate.validation(this.value, account);
          },
          get feedback() {
            return this.validation.regex.feedback || this.validation.accountDuplicate.feedback;
          },
          validation: {
            regex: {
              get feedback() {
                return this.valid ? '' : vm.$t('register.password.feedback.regex');
              },
              validation(val) {
                this.valid = PasswordFormatValidate(val);

                return this.valid;
              }
            },
            accountDuplicate: {
              get feedback() {
                return this.valid ? '' : vm.$t('register.password.feedback.accountDuplicate');
              },
              validation(val, account) {
                if (val === null || val === '') {
                  this.valid = true;
                } else if (val === account) {
                  this.valid = false;
                } else {
                  this.valid = true;
                }

                return this.valid;
              },
              valid: true
            }
          },
          dirty: false,
          valid: false,
          value: '',
          focus: false,
          showPassword: false
        },
        confirmPassword: {
          validate(origin) {
            return this.validation.equal.validation(this.value, origin);
          },
          get feedback() {
            return this.validation.equal.feedback;
          },
          validation: {
            equal: {
              get feedback() {
                return this.valid ? '' : vm.$t('register.confirmPassword.feedback.equal');
              },
              validation(val, origin) {
                this.valid = val === origin;
                return this.valid;
              }
            },
          },
          dirty: false,
          valid: false,
          value: '',
          focus: false,
          showPassword: false
        },
        get valid() {
          return this.account.valid && this.password.valid && this.confirmPassword.valid;
        },
        async validate() {
          await this.account.validation.duplicate.validation(this.account.value);
          this.account.valid = this.account.validation.duplicate.valid && this.account.validate();
          this.password.valid = this.password.validate(this.account.value);
          this.confirmPassword.valid = this.confirmPassword.validate(this.password.value);
        }
      }
    };
  },
  methods: {
    // input focus 事件
    onFocus(target) {
      // 設置 input focus 以讓提示出現
      this.form[target].focus = true;
    },
    // 帳號輸入事件
    onAccountInput() {
      const vm = this;

      // 驗證長度
      vm.form.account.valid = vm.form.account.validate();

      clearTimeout(vm.debounce);
      if (vm.form.account.valid) {
        vm.debounce = setTimeout(async () => {
          vm.form.account.dirty = true;
          // 驗證是否已有重複帳號
          await vm.form.account.validation.duplicate.validation(vm.form.account.value);
          vm.form.account.valid = vm.form.account.validation.duplicate.valid && vm.form.account.validate();
        }, 500);
      }
    },
    // 帳號input blur 事件
    async onAccountBlur() {
      this.form.account.dirty = true;
      this.form.account.focus = false;
    },
    // 密碼輸入事件
    onPwdInput() {
      this.form.password.valid = this.form.password.validate(this.form.account.value);
    },
    // 密碼確認輸入事件
    onConfirmPwdInput() {
      this.form.confirmPassword.valid = this.form.confirmPassword.validate(this.form.password.value);
    },
    // 密碼/密碼確認 input blur 事件
    onBlur(target) {
      this.form[target].showPassword = false;
      this.form[target].dirty = true;
      this.form[target].focus = false;
    },
    // 切換密碼顯示
    toggleVisibility(target, event) {
      event.preventDefault();
      this.form[target].showPassword = !this.form[target].showPassword;
    },
    async submit(event) {
      event.preventDefault();
      await this.form.validate();

      if (this.form.valid) {
        try {
          await this.$loading(async () => {
            await PermissionService.RegisterAsync(this.code, this.form.account.value, this.form.password.value);

            // captcha 驗證
            const captchaValidation = await this.$refs.captcha.VerifyAsync();
            let loginStatus = await LoginAsync(this.form.account.value, this.form.password.value, captchaValidation);

            if (loginStatus === LoginStatusType.AlreadyLogin) {
              loginStatus = await RefreshAsync();
            }
            if (loginStatus === LoginStatusType.Success) {
              this.$router.push({ path: this.redirect || '/' });
            }
          });
        } catch (e) {
          const errorMessage = e.response.data && e.response.data.message;
          await this.$popup.confirmAsync(errorMessage);
          throw errorMessage;
        }
      }
    }
  }
};
</script>
